<template>
  <div
    class="header-account-component"
    :class="{
      calculate: props.calculate,
    }">
    <div v-if="!isLoggedIn" :key="key" class="hidden lg:flex gap-4">
      <NuxtLink :to="getLocalizedRoute(Routes.Login)" class="btn-secondary w-full md:w-max" type="button">
        {{ t('auth.login') }}
      </NuxtLink>
      <NuxtLink :to="getLocalizedRoute(Routes.Register)" class="btn-primary w-full md:w-max" type="button">
        {{ t('auth.register') }}
      </NuxtLink>
    </div>
    <ClientOnly v-else>
      <template #fallback>
        <div class="account-toggle" :class="{ active: visible, '!rounded-2xl': !isLoggedIn }">
          <span class="account-icon" v-html="AccountIconRaw" />
          <div v-if="!isLoggedIn && !isMobile" class="flex flex-col pl-1 pr-3">
            <span class="text-xs text-gray6" :class="{ 'text-gray7': visible }">{{ t('auth.register') }}</span>
            <span class="text-sm alt:text-white">{{ t('auth.login') }}</span>
          </div>
        </div>
      </template>

      <UPopover
        v-model:open="visible"
        :popper="{ placement: 'bottom-end' }"
        :ui="{
          base: 'dropdown-body',
          container: 'dropdown',
        }">
        <div class="account-toggle" :class="{ active: visible, '!rounded-2xl': !isLoggedIn }">
          <span class="account-badge" />
          <span class="account-icon" v-html="AccountIconRaw" />
        </div>
        <template #panel>
          <div class="account-dropdown">
            <ClientOnly>
              <AccountMenu show-pro is-dropdown @on-close="visible = false" />
            </ClientOnly>
          </div>
        </template>
      </UPopover>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { useLang } from '~/composables/useLang'
import { useDropdownStore } from '~/store/dropdownStore'
import AccountIconRaw from '~/assets/icons/icon-person.svg?raw'
import { Routes } from '~/enums/Routes'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { useIdentity } from '~/store/identity'
import AccountMenu from '~/components/Account/AccountMenu.vue'
import { useStoreConfig } from '~/store/storeConfig'

const { isMobile } = useStoreConfig()

const props = defineProps({
  calculate: {
    type: Boolean,
    default: false,
    required: false,
  },
})

const { t } = useLang()
const identity = useIdentity()
await identity.getUser()

const isLoggedIn = computed(() => {
  return !!identity.user
})

const visible = ref<boolean>(false)

const key = computed(() => `${props.calculate}-${identity.user?.email}-${!isMobile}`)

const dropdownStore = useDropdownStore()

function clickEventListener(event: MouseEvent | PointerEvent) {
  const element = document.querySelector('.header-account-component')
  if (event.target instanceof Element) {
    if (
      element &&
      (!element.contains(event.target) ||
        event.target instanceof HTMLAnchorElement ||
        event.target instanceof HTMLButtonElement)
    ) {
      visible.value = false
    }
  }
}

watch(visible, (newValue) => {
  if (newValue) {
    emit('closeMenu')
    emit('closeSearch')
    dropdownStore.open('header_account')
    window?.addEventListener('click', clickEventListener)
  } else {
    window?.removeEventListener('click', clickEventListener)
  }
})

watch(dropdownStore.dropdowns, (newValue) => {
  visible.value = newValue.header_account
})

const emit = defineEmits(['closeMenu', 'closeSearch'])
</script>

<style lang="postcss" scoped>
.header-account-component {
  @apply flex gap-4;

  :deep(.account-toggle) {
    @apply flex h-10 bg-no-repeat bg-center relative rounded-full items-center hover:no-underline hover:bg-neutral7 select-none p-0;

    &.active {
      @apply bg-brand-primaryLight;

      .account-icon {
        svg {
          path {
            @apply fill-brand-primaryHover;
          }
        }
      }
    }

    .account-icon {
      @apply flex w-10 h-10 items-center justify-center;
    }
  }
}

.header-account-component {
  :deep(.dropdown) {
    width: 23.375rem;
    max-width: 100vw;
    z-index: 22;

    .arrow {
      display: none;
    }

    .dropdown-body {
      background: theme('colors.white');
      box-shadow: 0 4px 10px rgba(205, 205, 205, 0.5);
      border: 0;
      padding: 1.25rem;
    }

    @media not all and (min-width: calc(theme('screens.md') - 1px)) {
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      width: 100%;
      transform: none !important;
      top: theme('vars.mobileHeaderHeight') !important;

      .dropdown-body {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }

    .account-badge {
      position: absolute;
      right: 3px;
      top: 3px;
      width: 8px;
      height: 8px;
      background-color: theme('colors.brand.primary');
      border-radius: 100%;
    }
  }
}

.wl {
  .header-account-component {
    :deep(.sidebar-balance) {
      @apply mb-0 border-t border-neutral5 rounded-none p-4 rounded-b-sm;
    }

    .account-dropdown {
      @apply gap-2;
    }

    :deep(.account-toggle) {
      @apply !rounded-sm;

      .account-badge {
        @apply bg-red;
      }

      .account-icon {
        svg {
          path {
            @apply fill-brand-primary;
          }
        }
      }

      &:hover {
        .account-icon svg path {
          @apply fill-brand-black;
        }

        span {
          @apply text-blue;
        }
      }

      &.active {
        @apply !bg-brand-bgLight;

        span {
          @apply text-blue;
        }
      }
    }

    :deep(.dropdown) {
      .dropdown-body {
        @apply bg-brand-bgLight p-0 shadow-lg;

        > .account-dropdown {
          @apply lg:border lg:border-neutral5 lg:rounded-sm;
        }
      }
    }

    &.calculate {
      :deep(.dropdown-wrapper) {
        .account-icon {
          svg {
            path {
              @apply fill-black;
            }
          }
        }
      }
    }
  }
}
</style>
